/**
 * A recording's blocks, read back as the turns a person sees.
 *
 * The backend stores one paragraph block per speaker turn, with the speaker and the timecode on
 * the block's first line — `Speaker 1 · 00:12:03–00:12:41`, then the words. Two things complicate
 * reading that back, and both live here rather than in the dialog's JSX:
 *
 * - **A long turn is more than one block.** The store bounds every block's rendered size, so a
 *   twenty-minute monologue arrives as several, and only the *first* of them carries the header.
 *   A block whose first line is not a header is therefore a continuation of the turn above it,
 *   never a turn of its own.
 * - **Not every turn has a header at all.** An undiarized recording with no offsets produces
 *   blocks that are nothing but words, and a transcript may open with words before any header has
 *   been seen. Those are turns too; they simply have no chrome.
 *
 * The header's format is *not* re-implemented here. `parseTranscriptTurnHeader` (`@repo/shared`)
 * is the other half of the one body the ingestion writes with, which is what stops the reader and
 * the writer drifting apart over transcripts already stored. Its strictness is the point: an
 * ordinary sentence opening "Speaker of the house…" parses to nothing and stays words.
 */

import {
  formatTranscriptTurnHeader,
  parseTranscriptTurnHeader,
  type TranscriptSpeakerNames,
  type TranscriptTurnHeaderFacts,
} from "@repo/shared";

/** One stored block, as the transcript query hands it over. */
export type TranscriptBlock = {
  blockIndex: number;
  blockType: string;
  text: string;
};

export type TranscriptTurn = {
  /**
   * The index of the block the turn started at — stable while later pages append, and unique
   * within one transcript, which is what a list key needs.
   */
  key: number;
  /** What the turn's header line said, or `null` for a turn that carried none. */
  header: TranscriptTurnHeaderFacts | null;
  /** The words, with the header line taken off. */
  text: string;
};

export type TranscriptDocument = {
  /**
   * The note the ingestion opens every transcript with — how long the recording was, how many
   * speakers it told apart. It is a sentence *about* the document rather than one *of* it, which
   * is why it is a `metadata` block and why it is not a turn.
   *
   * It is what the **downloaded file** opens with, and only that: the viewer states the same
   * facts as labelled pairs in its own header, where a text file has nothing to state them in.
   */
  note: string | null;
  turns: TranscriptTurn[];
  /**
   * Any turn names a speaker.
   *
   * The whole presentation forks on this: a diarized transcript is drawn as turn-taking, and an
   * undiarized one as a reading column with no speaker chrome — because chrome that says nothing
   * is worse than no chrome, not equal to it.
   */
  diarized: boolean;
};

/** A turn's fragments, joined at the boundary the store drew rather than at a guessed one. */
const CONTINUATION_JOIN = "\n";

/**
 * Group a transcript's blocks into turns, in block order.
 *
 * Called again on every page the dialog appends, over the whole accumulated list rather than the
 * new page alone: a turn can straddle a page boundary, and grouping page by page would cut it in
 * two at whichever block the pager happened to stop on.
 */
export function groupTranscriptTurns(blocks: readonly TranscriptBlock[]): TranscriptDocument {
  const notes: string[] = [];
  const turns: TranscriptTurn[] = [];

  for (const block of blocks) {
    if (block.blockType === "metadata") {
      if (block.text.trim().length > 0) notes.push(block.text.trim());
      continue;
    }
    const [firstLine = "", ...rest] = block.text.split("\n");
    const header = parseTranscriptTurnHeader(firstLine);
    if (header !== null) {
      turns.push({ key: block.blockIndex, header, text: rest.join("\n").trim() });
      continue;
    }
    const open = turns[turns.length - 1];
    if (open === undefined) {
      // Words before any header: a transcript with no chrome at all, or the opening of one whose
      // first turn the vendor labelled nothing on.
      turns.push({ key: block.blockIndex, header: null, text: block.text.trim() });
      continue;
    }
    open.text =
      open.text.length === 0
        ? block.text.trim()
        : `${open.text}${CONTINUATION_JOIN}${block.text.trim()}`;
  }

  return {
    note: notes.length === 0 ? null : notes.join("\n"),
    turns,
    diarized: turns.some((turn) => turn.header?.speaker !== undefined),
  };
}

/**
 * The transcript as a plain-text file: the opening note, then every turn as its header line and
 * its words, one blank line apart.
 *
 * The header is written by the same function the ingestion writes blocks with, so a downloaded
 * transcript and a stored one spell a turn identically — a file somebody diffs against a search
 * hit has to match it. `names` is the one thing that may differ, and it must: a file saved off a
 * transcript whose speakers have been named has to say what the reader was looking at, or the
 * download and the dialog disagree about who spoke.
 */
export function buildTranscriptText(
  document: TranscriptDocument,
  names?: TranscriptSpeakerNames
): string {
  const sections: string[] = [];
  if (document.note !== null) sections.push(document.note);
  for (const turn of document.turns) {
    const header = turn.header === null ? null : formatTranscriptTurnHeader(turn.header, names);
    sections.push(header === null ? turn.text : `${header}\n${turn.text}`);
  }
  // A trailing newline, because a text file's last line ends like every other one.
  return `${sections.filter((section) => section.length > 0).join("\n\n")}\n`;
}

/**
 * What the saved file is called: the document's name with the extension it actually is.
 *
 * A recording is `standup.mp4` and its transcript is not, so the media extension is dropped
 * rather than kept in front of `.txt` — `standup.mp4.transcript.txt` names a file that claims to
 * be two things.
 */
export function transcriptFileName(name: string): string {
  const base = name.replace(/\.[^./\\]{1,12}$/, "").trim();
  return `${base.length === 0 ? name : base}.transcript.txt`;
}
